// src/features/atividade/polyline.js
//
// Encoded Polyline Algorithm Format (o do Google), precisão 5.
//
// POR QUE ISTO EXISTE. O brief original guardava `pontos[]` cru no documento
// do Firestore. Um par {lat, lng, alt, t, acc} em JSON custa ~90 bytes; uma
// pedalada de 2 h a 1 Hz são 7.200 pontos, 650 KB — e o teto do documento é
// 1 MB. A segunda pedalada longa da vida de alguém estouraria o limite e o
// save falharia com um erro que ninguém entende no meio da celebração.
//
// A mesma linha em polyline codificada custa ~4-6 bytes por ponto: os 7.200
// pontos viram ~35 KB. A perda é a quinta casa decimal, ~1,1 m no equador —
// menos que o erro do próprio GPS, que já foi filtrado a 30 m.
//
// Formato, para quem nunca leu: cada coordenada vira delta em relação à
// anterior (é onde mora quase toda a economia — deltas de trajeto são
// minúsculos), multiplicado por 1e5, arredondado, passado para complemento de
// dois deslocado à esquerda, e então cortado em grupos de 5 bits do menos
// significativo para o mais, cada grupo com o bit 0x20 ligado enquanto houver
// próximo, somando 63 para cair em ASCII imprimível.
//
// É o mesmo formato que Google Maps, Mapbox, Valhalla e OSRM falam, então o
// traçado guardado aqui pode ser lido por qualquer ferramenta de mapa sem
// conversão.

const PRECISAO = 1e5;

function codificarNumero(valor) {
  let v = valor < 0 ? ~(valor << 1) : valor << 1;
  let saida = '';
  while (v >= 0x20) {
    saida += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
    v >>= 5;
  }
  saida += String.fromCharCode(v + 63);
  return saida;
}

/**
 * Lista de pontos → string codificada.
 *
 * @param {Array<{lat:number,lng:number}>} pontos
 * @returns {string} polyline (string vazia para lista vazia)
 */
export function codificar(pontos) {
  if (!Array.isArray(pontos) || !pontos.length) return '';

  let saida = '';
  let latAnterior = 0;
  let lngAnterior = 0;

  for (const p of pontos) {
    const lat = Math.round(Number(p?.lat) * PRECISAO);
    const lng = Math.round(Number(p?.lng) * PRECISAO);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;

    saida += codificarNumero(lat - latAnterior);
    saida += codificarNumero(lng - lngAnterior);
    latAnterior = lat;
    lngAnterior = lng;
  }

  return saida;
}

/**
 * String codificada → lista de pontos.
 *
 * @param {string} texto
 * @returns {Array<{lat:number,lng:number}>}
 */
export function decodificar(texto) {
  if (typeof texto !== 'string' || !texto) return [];

  const pontos = [];
  let i = 0;
  let lat = 0;
  let lng = 0;

  while (i < texto.length) {
    let resultado = 1;
    let deslocamento = 0;
    let b;

    do {
      b = texto.charCodeAt(i) - 63 - 1;
      i += 1;
      resultado += b << deslocamento;
      deslocamento += 5;
    } while (b >= 0x1f && i < texto.length);
    lat += resultado & 1 ? ~(resultado >> 1) : resultado >> 1;

    resultado = 1;
    deslocamento = 0;
    do {
      b = texto.charCodeAt(i) - 63 - 1;
      i += 1;
      resultado += b << deslocamento;
      deslocamento += 5;
    } while (b >= 0x1f && i < texto.length);
    lng += resultado & 1 ? ~(resultado >> 1) : resultado >> 1;

    pontos.push({ lat: lat / PRECISAO, lng: lng / PRECISAO });
  }

  return pontos;
}

/**
 * Caixa delimitadora de uma lista de pontos.
 *
 * Serve para dois usos: enquadrar o mapa (`fitBounds`) e desenhar a miniatura
 * SVG da lista sem depender de mapa nenhum.
 *
 * @param {Array<{lat:number,lng:number}>} pontos
 * @returns {{minLat:number,minLng:number,maxLat:number,maxLng:number}|null}
 */
export function limites(pontos) {
  if (!Array.isArray(pontos) || !pontos.length) return null;
  let minLat = Infinity;
  let minLng = Infinity;
  let maxLat = -Infinity;
  let maxLng = -Infinity;

  for (const p of pontos) {
    const la = Number(p?.lat);
    const ln = Number(p?.lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) continue;
    if (la < minLat) minLat = la;
    if (la > maxLat) maxLat = la;
    if (ln < minLng) minLng = ln;
    if (ln > maxLng) maxLng = ln;
  }

  if (minLat === Infinity) return null;
  return { minLat, minLng, maxLat, maxLng };
}

/**
 * Ramer–Douglas–Peucker: joga fora os pontos que não mudam o desenho.
 *
 * Usado na MINIATURA da lista, não no traçado guardado. Um item de 84px não
 * distingue 7.000 pontos de 60, e mandar 7.000 comandos de `path` por item
 * numa lista rolável é o caminho curto para a rolagem travar.
 *
 * Tolerância em GRAUS (a miniatura não precisa de metros): 1e-4 ≈ 11 m.
 *
 * @param {Array<{lat:number,lng:number}>} pontos
 * @param {number} tolerancia
 * @returns {Array<{lat:number,lng:number}>}
 */
export function simplificar(pontos, tolerancia = 1e-4) {
  if (!Array.isArray(pontos) || pontos.length < 3) return pontos || [];

  const distanciaPerpendicular = (p, a, b) => {
    const dx = b.lng - a.lng;
    const dy = b.lat - a.lat;
    if (dx === 0 && dy === 0) return Math.hypot(p.lng - a.lng, p.lat - a.lat);
    const t = ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / (dx * dx + dy * dy);
    const tc = Math.max(0, Math.min(1, t));
    return Math.hypot(p.lng - (a.lng + tc * dx), p.lat - (a.lat + tc * dy));
  };

  // Iterativo, não recursivo: um trajeto longo e cheio de curvas chega a
  // milhares de níveis de recursão e estoura a pilha no WebView.
  const manter = new Uint8Array(pontos.length);
  manter[0] = 1;
  manter[pontos.length - 1] = 1;
  const pilha = [[0, pontos.length - 1]];

  while (pilha.length) {
    const [ini, fim] = pilha.pop();
    let maior = 0;
    let indice = -1;
    for (let i = ini + 1; i < fim; i += 1) {
      const d = distanciaPerpendicular(pontos[i], pontos[ini], pontos[fim]);
      if (d > maior) {
        maior = d;
        indice = i;
      }
    }
    if (maior > tolerancia && indice > 0) {
      manter[indice] = 1;
      pilha.push([ini, indice], [indice, fim]);
    }
  }

  return pontos.filter((_, i) => manter[i]);
}

export default { codificar, decodificar, limites, simplificar };
